import { DynamicUxClientState } from '@ms-ofb/floodgate-shared-typings';
import { Utils } from '@ms-ofb/officefloodgatecore';
import { IAdapterProviderV3, IPreConfig } from '@ms/centro-hvc-loader';
import { BackendTarget, IRequestOptionsCombined } from '@ms/centro-hvc-loader/dist/runtime/data/requestFunction.types';
import * as Configuration from "../../Configuration/Configuration";
import { IDynamicUxConfig } from "../../Configuration/IInitOptions";
import IUISurvey from '../../FloodgateCore/UISurvey/IUISurvey';
import * as Logging from "../../Logging/Logging";
import { getOrCreateDiv } from '../Utils';
import {
	CentroCommonInitOptions,
	CentroFloodgateContainerId,
	CentroFloodgateFeatureName,
	CentroFloodgateHostName,
	CentroFloodgateInitOptions,
	CentroFloodgatePerfPrefix,
	CentroPetrolUploadEndpoint,
	CentroPreloadLoggingSamplingRate,
	CentroThemeInitOptions,
	CentroUrlProd,
	FloodgateCentroVersion
} from './CentroConstants';
import { CentroCoreDataSource } from './CentroCoreDataSource';
import { CentroLoaderWrapper } from './CentroLoaderWrapper';
import { centroLogging } from './CentroLogger';
import { CentroThemeDataSource } from './CentroThemeDataSource';


export class DynamicUxClient {

	// making these data public so they can be access for testing
	public clientState: DynamicUxClientState;

	// Configuration data
	private dynamicUxConfig: IDynamicUxConfig;
	private userSurveyCallback: (status: DynamicUxClientState) => void;
	private systemSurveyCallback: (status: DynamicUxClientState) => void;
	private boostStrapper;
	private unmountFunc;
	private elementToMount: HTMLElement;

	constructor() {
		this.dynamicUxConfig = {};
		this.clientState = DynamicUxClientState.NotStarted;
		const surveyContainer = Configuration.get().getFloodgateInitOptions().surveyContainer;

		if (Utils.isNOU(surveyContainer)) {
			// Can't use surveyContainer since it's null or undefined, so use default element
			this.elementToMount = getOrCreateDiv(CentroFloodgateContainerId, true, false);
		} else if (typeof surveyContainer === 'string') {
			// Element ID is provided
			this.elementToMount = getOrCreateDiv(surveyContainer, true, false);
		} else if (typeof surveyContainer == 'object') { // TypeOf surveyContainer is "Object" when HtmlElement is passed, so checking for instanceOf
			// HtmlElement is provided
			this.elementToMount = surveyContainer;
		} else {
			// Can't use surveyContainer, so use default element
			this.elementToMount = getOrCreateDiv(CentroFloodgateContainerId, true, false);
		}
	}

	public setup(config: IDynamicUxConfig) {
		this.dynamicUxConfig = config;
	}

	public isEnabled(): boolean {
		return !!this.dynamicUxConfig.enabled;
	}

	public getStatus(): DynamicUxClientState {
		return this.clientState;
	}

	public delayLoadCentroUx() {
		if (this.dynamicUxConfig.delayLoadingTimeout === 0) {
			Logging.getLogger().logEvent(Logging.EventIds.DYNAMICUX_LOAD_PRELOAD,
				Logging.LogLevel.Critical,
				{
					TimeMilliseconds: 0
				},
				CentroPreloadLoggingSamplingRate
			);
			this.loadCentroUx();
		}
		else {
			const timeBefore = performance.now();
			setTimeout(() => {
				const waitingTime = performance.now() - timeBefore;
				Logging.getLogger().logEvent(Logging.EventIds.DYNAMICUX_LOAD_PRELOAD,
					Logging.LogLevel.Critical,
					{
						TimeMilliseconds: waitingTime
					},
					CentroPreloadLoggingSamplingRate
				);
				this.loadCentroUx();
			}, this.dynamicUxConfig.delayLoadingTimeout);
		}
	}

	/**
	 * This function should only be called prior to the DynamicUxClient status check
	 *  to be Starting, so it can set the callback to be performed once the loading is done
	 */
	public setOnSurveyActivatedCallback(callback: (status: DynamicUxClientState) => void, survey: IUISurvey) {
		Logging.getLogger().logEvent(Logging.EventIds.SURVEY_TRIGGER_DYNAMICUX_ONLOAD,
			Logging.LogLevel.Critical,
			{
				CampaignId: survey.getCampaignId(),
				SurveyId: survey.getId(),
				SurveyType: survey.getSurveyType(),
				Message: JSON.stringify({
					DynamicUxClientState: this.clientState,
					isUserInitiatedSurvey: survey.isUserInitiatedSurvey()
				}),
			}
		);
		if (survey.isUserInitiatedSurvey()) {
			this.userSurveyCallback = callback;
		}
		else {
			this.systemSurveyCallback = callback;
		}
	}

	public async boostStrapSurvey() {
		if (this.clientState !== DynamicUxClientState.Success) {
			return Promise.reject("DynamicUx is not loaded successfully");
		}

		try {
			this.unmountFunc = await this.boostStrapper.main(this.elementToMount);
		}
		catch (error) {
			return Promise.reject(`Error: ${error.toString()}, Error stack: ${error.stack}`);
		}
	}

	public unmountSurvey() {
		if (this.unmountSurvey) {
			this.unmountFunc();
		}
	}

	private async loadCentroUx() {
		// If the Centro is already loaded successfully,
		// or previous load failed or is loading,
		// return right away.
		if (this.clientState === DynamicUxClientState.Success || this.clientState === DynamicUxClientState.Starting ||
			this.clientState === DynamicUxClientState.Failure) {
			return;
		}

		this.clientState = DynamicUxClientState.Starting;
		const timeBefore = performance.now();
		try {
			const preConfig: IPreConfig = {
				hostName: Configuration.get().getFloodgateInitOptions().dynamicUxConfig.hostName || CentroFloodgateHostName,
				locale: Configuration.get().getCommonInitOptions().locale,
				// All perf log will be prefixed with this string.
				// So that when we analysis perf logs, we know that those logs are coming from HVC running in your environment.
				perfPrefix: CentroFloodgatePerfPrefix,
				additionalLoggers: { CentroFloodgateLoggerFunc: centroLogging },
				kustoEndpoint: '',
				// Disable Centro aria logger due to the issue with it setting unsecure cookie.
				disableAriaLogging: true,
			};

			const centroParams = {
				// Please use Centro Prod Url even for development to get the latest released of the HVC
				// Centro CI Url can also be used for testing unreleased features
				centroEnvironmentBaseUrl: CentroUrlProd,
				preConfig,
				targetHtmlElement: this.elementToMount,
				forHostVersion: FloodgateCentroVersion // using Floodgate Centro version since Centro only support exact match
			};

			await CentroLoaderWrapper.initialize(centroParams);

			const dataSources = sd => {
				sd(CentroCommonInitOptions, CentroLoaderWrapper.createStaticDataSource({ theme: 'M365Light' })), // unused but required by Centro
					sd(CentroFloodgateInitOptions, CentroCoreDataSource.get())
				sd(CentroThemeInitOptions, CentroThemeDataSource.get())
			};

			// Adapter for custom request in Centro. However, we don't have any.
			// So this is basically return empty object or original URL
			const centroAdapters: IAdapterProviderV3 = {
				request: {
					getAuthToken: () => Promise.resolve(null),
					urlResolveHandler: (reqOpt: IRequestOptionsCombined) => {
						if (reqOpt.url.includes(CentroPetrolUploadEndpoint)) {
							return reqOpt.url;
						}

						if (reqOpt.backend === BackendTarget.M365AdminCenter) {
							return CentroUrlProd + reqOpt.url;
						}
						return reqOpt.url;
					},
				},
			};

			await CentroLoaderWrapper.preloadFeature({
				featureName: CentroFloodgateFeatureName,
				adapters: { ...centroAdapters, dataSources },
			});

			this.boostStrapper = await CentroLoaderWrapper.bootstrapFeature({
				featureName: CentroFloodgateFeatureName
			});

			this.clientState = DynamicUxClientState.Success;
			const serviceCallTime = performance.now() - timeBefore;
			Logging.getLogger().logEvent(Logging.EventIds.DYNAMICUX_LOAD_SUCCESS,
				Logging.LogLevel.Critical,
				{
					TimeMilliseconds: serviceCallTime,
				}
			);
		}
		catch (error) {
			this.clientState = DynamicUxClientState.Failure;
			const serviceCallTime = performance.now() - timeBefore;
			Logging.getLogger().logEvent(Logging.EventIds.DYNAMICUX_LOAD_FAILURE,
				Logging.LogLevel.Error,
				{
					TimeMilliseconds: serviceCallTime,
					ErrorMessage: `Error: ${error.toString()}, Error stack: ${error.stack}`,
				}
			);
			Configuration.get().getCommonInitOptions().onError(`Failed to download Dynamic survey UX: ${error}`);
		}

		// Perform the callback if there is any survey waiting
		// The user-initiated-survey has higher priority than system-initated-survey
		if (this.userSurveyCallback) {
			this.userSurveyCallback(this.clientState);
			this.userSurveyCallback = undefined;
			return;
		}

		if (this.systemSurveyCallback) {
			this.systemSurveyCallback(this.clientState);
			this.systemSurveyCallback = undefined;
			return;
		}
	}
}

let dynamicUxClient;

/**
 * Get the current Dynamic Ux Client
 */
export function get(): DynamicUxClient {
	return dynamicUxClient;
}

/**
 * Setup the Dynamic Ux Client
 */
export function setup(config: IDynamicUxConfig) {
	if (!dynamicUxClient) {
		dynamicUxClient = new DynamicUxClient();
	}
	dynamicUxClient.setup(config);
}
